import { useEffect, useId, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { SectionCard } from "@/base";
import {
  formatChangePercent,
  formatStatementMoney,
  getAccountFinancialSummary,
  type FinancialStatement,
  type FinancialSummary,
} from "@/lib/accountFinancials";
import type { AccountDetail } from "@/lib/brokerAccounts";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { SignalAdvanceApplication } from "@/components/SignalAdvanceApplication";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S409 — the Surety account's Financials tab: the insured's financial
// SUMMARY (PMS-S853, src/lib/accountFinancials.ts). Since
// BRK-S501 it is the October wireframe's tab.
//
// Two states, both PMS's to decide: NOT CONNECTED shows the Signal Advance
// application card (the program the connected books support, with "Apply for
// Signal Advance →") and then "No financials yet"; CONNECTED shows the income
// statement and balance sheet D3 lets a broker see — the wireframe's six and
// ten lines, with cents, dated columns and the server's change percentage —
// and nothing else: no ratio, no score, no tier, no review reason.
//
// The SPA renders, never decides (NN #7): every date and change is served, a
// figure not served as a number reads "—" (never zero), and a failed or
// unreadable read is an error with Retry — never an empty table posing as a
// business with no money.
//
// Every state sits in the SAME panel well as the account's other tabs
// (SectionCard), reused, never restated (BRK-S499).

type State =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; summary: FinancialSummary };

export function AccountFinancialsPanel({ detail }: { detail: AccountDetail }) {
  const accountId = detail.id;
  const { authedRequest } = useAuth();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    getAccountFinancialSummary(authedRequest, accountId).then(
      (summary) => {
        if (!cancelled) setState({ kind: "ready", summary });
      },
      () => {
        if (!cancelled) setState({ kind: "error" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [authedRequest, accountId, attempt]);

  if (state.kind === "loading") {
    return (
      <p role="status" className="text-sm text-brk-dim">
        Loading financials…
      </p>
    );
  }
  if (state.kind === "error") {
    return (
      <SectionCard>
        <div role="alert" className="flex flex-col items-start gap-3 text-sm">
          <p className="text-brk-danger">
            We couldn’t load this insured’s financials.
          </p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className={buttonClass({ variant: "secondary", size: "sm" })}
          >
            Retry
          </button>
        </div>
      </SectionCard>
    );
  }
  const { summary } = state;
  if (!summary.connected) return <NotConnected detail={detail} />;
  const updated = summary.lastUpdated && formatBondDate(summary.lastUpdated);
  return (
    <SectionCard title="Financials">
      <div className="flex min-w-0 flex-col gap-6">
        <p className="text-sm text-brk-dim">
          A summary of the insured’s income statement and balance sheet.
          {updated && ` Last updated ${updated}.`}
        </p>
        <Statement
          title="Income statement"
          note="Both columns cover 12 months."
          priorLabel="Prior year end"
          currentLabel="Trailing 12 months"
          statement={summary.incomeStatement}
        />
        <Statement
          title="Balance sheet"
          note="Balances on each date."
          priorLabel="Prior year end"
          currentLabel="Current"
          statement={summary.balanceSheet}
        />
        <p className="text-xs text-brk-dim">
          Brokers see this summary only, not the full statements.
        </p>
      </div>
    </SectionCard>
  );
}

function NotConnected({ detail }: { detail: AccountDetail }) {
  const emptyId = useId();
  return (
    <SectionCard title="Financials">
      <div className="flex min-w-0 flex-col gap-5">
        <p className="text-sm text-brk-dim">
          A summary of the insured’s income statement and balance sheet.
        </p>
        <SignalAdvanceApplication
          detail={detail}
          apply
          onRefreshed={() => undefined}
          onChanged={() => undefined}
        />
        <section
          aria-labelledby={emptyId}
          className="flex flex-col gap-1 rounded-lg border border-dashed border-brk-border p-4 text-center"
        >
          <h3 id={emptyId} className="text-sm font-semibold text-brk-text">
            No financials yet
          </h3>
          <p className="text-sm text-brk-dim">
            The income statement and balance sheet fill in after the client
            connects their bank and accounting system.
          </p>
        </section>
      </div>
    </SectionCard>
  );
}

/** A served date range (`from`–`to`) or date, m/d/yyyy (D-10). */
function dates({ from, to }: { from: string | null; to: string }): string {
  return from
    ? `${formatBondDate(from)} – ${formatBondDate(to)}`
    : formatBondDate(to);
}

function Statement({
  title,
  note,
  priorLabel,
  currentLabel,
  statement,
}: {
  title: string;
  note: string;
  priorLabel: string;
  currentLabel: string;
  statement: FinancialStatement;
}) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className="flex min-w-0 flex-col gap-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h3 id={headingId} className="text-subheading text-brk-text">
          {title}
        </h3>
        <p className="text-xs text-brk-dim">{note}</p>
      </div>
      {/* Four columns of money cannot shrink to 375px: the table scrolls
          inside its own box rather than pushing the page sideways. */}
      <div className="overflow-x-auto">
        <table aria-label={title} className="w-full min-w-[36rem] text-sm">
          <thead>
            <tr className="border-b border-brk-border text-left text-xs text-brk-dim">
              <th scope="col" className="px-3 py-2 font-medium">
                <span className="sr-only">Line item</span>
              </th>
              <ColumnHeader label={priorLabel} dates={dates(statement.prior)} />
              <ColumnHeader
                label={currentLabel}
                dates={dates(statement.current)}
              />
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Change
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brk-border">
            {statement.rows.map((r) => (
              <tr
                key={r.key}
                className={r.subtotal ? "font-semibold" : undefined}
              >
                <th
                  scope="row"
                  className={`px-3 py-2 text-left text-brk-text ${r.subtotal ? "font-semibold" : "font-normal"}`}
                >
                  {r.label}
                </th>
                <td className="px-3 py-2 text-right tabular-nums text-brk-text">
                  {r.prior === null ? "—" : formatStatementMoney(r.prior)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-brk-text">
                  {r.current === null ? "—" : formatStatementMoney(r.current)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-brk-text">
                  {r.changePercent === null
                    ? "—"
                    : formatChangePercent(r.changePercent)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ColumnHeader({ label, dates }: { label: string; dates: string }) {
  return (
    <th scope="col" className="px-3 py-2 text-right font-medium">
      <span className="block">{label}</span>
      <span className="block font-normal">{dates}</span>
    </th>
  );
}
